import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Search,
  Plane,
  Calendar,
  Clock,
  Users,
  Printer,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Mail,
  Send,
  X,
  Globe,
  Database,
  Radio
} from 'lucide-react';
import axios from 'axios';
import { downloadETicketPDF, printETicket } from '../utils/pdfGenerator';
import { FlightStatusTracker } from './FlightStatusTracker';
import { Money } from '../contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GDS_OPTIONS = [
  { value: 'local', label: 'Flight380 Database', icon: Database, color: 'bg-purple-500' },
  { value: 'sabre', label: 'Sabre GDS', icon: Globe, color: 'bg-red-500' },
  { value: 'airline', label: 'Airline Website', icon: Plane, color: 'bg-slate-800' }
];

// Direct deep-links to each airline's Manage My Booking portal. Most airlines
// don't support URL-based pre-fill (their forms use CSRF tokens), so we just
// open the correct page and display the PNR/surname clearly on our side for
// the customer to paste. Where a carrier supports query-string pre-fill, the
// URL includes {PNR}/{SURNAME} placeholders that we substitute at click-time.
const AIRLINE_MANAGE_URLS = [
  { code: 'SV', name: 'Saudia',                    url: 'https://www.saudia.com/trip-manager?recordLocator={PNR}&lastName={SURNAME}' },
  { code: 'EK', name: 'Emirates',                  url: 'https://www.emirates.com/english/manage-booking/manage-booking.aspx' },
  { code: 'QR', name: 'Qatar Airways',             url: 'https://www.qatarairways.com/en/manage-booking.html' },
  { code: 'EY', name: 'Etihad Airways',            url: 'https://www.etihad.com/en-gb/manage-bookings' },
  { code: 'TK', name: 'Turkish Airlines',          url: 'https://www.turkishairlines.com/en-int/flights/manage-booking/' },
  { code: 'BA', name: 'British Airways',           url: 'https://www.britishairways.com/travel/managebooking/public/en_gb' },
  { code: 'PK', name: 'Pakistan International',    url: 'https://book.piac.com.pk/manageBooking.aspx' },
  { code: 'XY', name: 'flynas',                    url: 'https://www.flynas.com/en/manage-booking' },
  { code: 'G9', name: 'Air Arabia',                url: 'https://www.airarabia.com/en/manage-my-booking' },
  { code: 'MS', name: 'EgyptAir',                  url: 'https://www.egyptair.com/en/fly/manage-booking/Pages/managebooking.aspx' },
  { code: 'GF', name: 'Gulf Air',                  url: 'https://www.gulfair.com/manage-booking' },
  { code: 'WY', name: 'Oman Air',                  url: 'https://www.omanair.com/en/manage-booking' },
  { code: 'KU', name: 'Kuwait Airways',            url: 'https://www.kuwaitairways.com/en/manage-my-booking' },
  { code: 'RJ', name: 'Royal Jordanian',           url: 'https://www.rj.com/en/managebooking' },
  { code: 'ME', name: 'Middle East Airlines',      url: 'https://www.mea.com.lb/english/plan-and-book/manage-my-booking' },
  { code: 'FZ', name: 'flydubai',                  url: 'https://www.flydubai.com/en/manage' },
  { code: 'AI', name: 'Air India',                 url: 'https://www.airindia.com/in/en/manage/manage-booking.html' },
  { code: 'LH', name: 'Lufthansa',                 url: 'https://www.lufthansa.com/gb/en/flight-manager' },
  { code: 'KL', name: 'KLM',                       url: 'https://www.klm.com/travel/gb_en/prepare_for_travel/checkin_options/mytrip/index.htm' },
  { code: 'AF', name: 'Air France',                url: 'https://wwws.airfrance.co.uk/manage/identification' },
  { code: 'VS', name: 'Virgin Atlantic',           url: 'https://www.virginatlantic.com/gb/en/manage-my-booking.html' },
];

export const ManageBooking = ({ onBack }) => {
  const [pnr, setPnr] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedSource, setSelectedSource] = useState('local');
  const [selectedAirline, setSelectedAirline] = useState('SV');
  const [showFlightStatus, setShowFlightStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [searchSource, setSearchSource] = useState(null);
  
  // Email functionality state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!pnr.trim()) {
      setError('Please enter a booking reference (PNR)');
      return;
    }

    // Require last name for both local database and Sabre GDS
    if (!lastName.trim()) {
      setError('Please enter the passenger last name');
      return;
    }

    // Airline Website tab — just open the airline's manage-booking page
    // in a new tab. Where the airline supports URL pre-fill we substitute
    // the PNR/surname; otherwise the customer copies from the on-page banner.
    if (selectedSource === 'airline') {
      const airline = AIRLINE_MANAGE_URLS.find(a => a.code === selectedAirline);
      if (!airline) {
        setError('Please pick your airline.');
        return;
      }
      const url = airline.url
        .replace('{PNR}', encodeURIComponent(pnr.trim().toUpperCase()))
        .replace('{SURNAME}', encodeURIComponent(lastName.trim().toUpperCase()));
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    setIsLoading(true);
    setError('');
    setBooking(null);
    setSearchSource(null);

    try {
      if (selectedSource === 'local') {
        // Search local database
        const response = await axios.get(`${BACKEND_URL}/api/bookings/retrieve`, {
          params: {
            pnr: pnr.trim().toUpperCase(),
            last_name: lastName.trim().toUpperCase()
          }
        });

        if (response.data.success) {
          setBooking(response.data.booking);
          setSearchSource('local');
        } else {
          setError(response.data.message || 'Booking not found');
        }
      } else {
        // Search GDS (Sabre) - pass last_name for verification
        const response = await axios.post(`${BACKEND_URL}/api/gds/retrieve-pnr`, {
          pnr: pnr.trim().toUpperCase(),
          gds: selectedSource,
          last_name: lastName.trim().toUpperCase()
        });

        if (response.data.success) {
          // Transform GDS response to booking format
          const gdsBooking = response.data.booking;
          const transformedBooking = {
            pnr: gdsBooking.pnr,
            passengers: gdsBooking.passengers || gdsBooking.local_data?.passengers || [],
            flight_data: gdsBooking.local_data?.flight_data || {
              segments: gdsBooking.segments || [],
              return_segments: gdsBooking.return_segments || [],
              fare_details: gdsBooking.fare_details || {},
              from: gdsBooking.segments?.[0]?.departure_airport || '',
              to: gdsBooking.segments?.[0]?.arrival_airport || '',
              departure_date: gdsBooking.segments?.[0]?.departure_date || '',
              departure_time: gdsBooking.segments?.[0]?.departure_time || '',
              arrival_time: gdsBooking.segments?.[0]?.arrival_time || ''
            },
            flight: gdsBooking.local_data?.flight_data || {
              segments: gdsBooking.segments || [],
              return_segments: gdsBooking.return_segments || []
            },
            contact: gdsBooking.contact || gdsBooking.local_data?.contact || {},
            total_price: gdsBooking.fare_details?.total_price || gdsBooking.local_data?.total_price || 0,
            status: gdsBooking.status || 'CONFIRMED',
            gds_source: response.data.gds,
            found_in_local_db: gdsBooking.found_in_local_db
          };
          setBooking(transformedBooking);
          setSearchSource(response.data.gds);
        } else {
          setError(response.data.message || 'PNR not found');
        }
      }
    } catch (err) {
      console.error('Booking retrieval error:', err);
      if (err.response?.status === 404) {
        setError('No booking found with the provided reference. Please check your details and try again.');
      } else {
        setError(err.response?.data?.message || 'Unable to retrieve booking. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const match = time.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : time;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const handlePrint = () => {
    if (!booking) return;
    printETicket(booking);
  };

  const handleDownloadPDF = async () => {
    if (!booking) return;
    
    setIsGeneratingPDF(true);
    try {
      await downloadETicketPDF(booking);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const resetSearch = () => {
    setBooking(null);
    setPnr('');
    setLastName('');
    setError('');
    setSearchSource(null);
  };

  // Email PDF function
  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSendingEmail(true);
    setEmailError('');
    setEmailSent(false);

    try {
      // Use flight_data first (has segments), fall back to flight
      const flight = booking.flight_data || booking.flight || {};
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/send-confirmation-email`, {
        pnr: booking.pnr,
        passengers: booking.passengers?.map(p => ({
          type: p.type || 'ADULT',
          title: p.title || 'MR',
          first_name: p.first_name || p.firstName || '',
          last_name: p.last_name || p.lastName || ''
        })) || [],
        flight_data: {
          airline_code: flight.airline_code || '',
          airline: flight.airline || '',
          flight_number: flight.flight_number || '',
          from: flight.from || flight.origin || '',
          to: flight.to || flight.destination || '',
          departure_date: flight.departure_date || '',
          departure_time: flight.departure_time || '',
          arrival_time: flight.arrival_time || '',
          duration: flight.duration || '',
          stops: flight.stops || 0,
          cabin_class: flight.cabin_class || 'Economy',
          return_airline_code: flight.return_airline_code || flight.airline_code || '',
          return_flight_number: flight.return_flight_number || '',
          return_departure_time: flight.return_departure_time || '',
          return_arrival_time: flight.return_arrival_time || '',
          return_departure_date: flight.return_departure_date || '',
          return_duration: flight.return_duration || '',
          return_stops: flight.return_stops || 0,
          is_combined: !!flight.return_departure_time,
          // Include segments for connecting flights
          segments: flight.segments || [],
          return_segments: flight.return_segments || []
        },
        contact_email: emailAddress,
        contact_phone: booking.contact?.phone || '',
        total_price: booking.total_price || booking.price || 0,
        currency: 'GBP'
      });

      if (response.data.success) {
        setEmailSent(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSent(false);
          setEmailAddress('');
        }, 2000);
      } else {
        setEmailError(response.data.message || 'Failed to send email');
      }
    } catch (err) {
      console.error('Email send error:', err);
      setEmailError('Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const openEmailModal = () => {
    setShowEmailModal(true);
    setEmailAddress(booking.contact?.email || '');
    setEmailError('');
    setEmailSent(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        {onBack && (
          <div className="mb-4">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="text-slate-600 hover:text-brand-600"
            >
              ← Back to Home
            </Button>
          </div>
        )}
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Manage Your Booking</h1>
          <p className="text-slate-600">Retrieve your itinerary from Flight380 or directly from airline systems</p>
        </div>

        {!booking ? (
          /* Search Form */
          <Card className="max-w-lg mx-auto shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-brand-500 to-brand-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Retrieve Booking
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSearch} className="space-y-4">
                {/* Source Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Search From</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {GDS_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setSelectedSource(option.value);
                            setError('');
                            setBooking(null);
                            setSearchSource(null);
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all justify-center ${
                            selectedSource === option.value
                              ? `${option.color} text-white shadow-md`
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`booking-source-${option.value}-btn`}
                        >
                          <Icon className="h-4 w-4" />
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pnr" className="text-sm font-medium">
                    Booking Reference (PNR)
                  </Label>
                  <Input
                    id="pnr"
                    placeholder="e.g., ABC123"
                    value={pnr}
                    onChange={(e) => setPnr(e.target.value.toUpperCase())}
                    className="uppercase font-mono text-lg tracking-wider"
                    maxLength={10}
                  />
                </div>
                
                {selectedSource === 'local' && (
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium">
                      Passenger Last Name
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="e.g., Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="capitalize"
                      data-testid="booking-lastname-input"
                    />
                  </div>
                )}

                {selectedSource === 'airline' && (
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium">
                      Passenger Last Name
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="e.g., Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="capitalize"
                      data-testid="booking-lastname-input"
                    />
                  </div>
                )}

                {selectedSource === 'airline' && (
                  <div className="space-y-2">
                    <Label htmlFor="airlinePicker" className="text-sm font-medium">
                      Airline
                    </Label>
                    <select
                      id="airlinePicker"
                      value={selectedAirline}
                      onChange={(e) => setSelectedAirline(e.target.value)}
                      className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      data-testid="airline-picker-select"
                    >
                      {AIRLINE_MANAGE_URLS.map((a) => (
                        <option key={a.code} value={a.code}>
                          {a.name} ({a.code})
                        </option>
                      ))}
                    </select>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs leading-relaxed">
                      <Plane className="h-4 w-4 inline mr-1 align-text-bottom" />
                      Airline PNRs must be managed on the airline's own site. We'll
                      open <b>{AIRLINE_MANAGE_URLS.find(a => a.code === selectedAirline)?.name}</b>'s
                      Manage My Booking page in a new tab. Have your PNR
                      <b> {pnr || '—'} </b> and last name <b>{lastName || '—'}</b> ready.
                    </div>
                  </div>
                )}

                {selectedSource !== 'local' && selectedSource !== 'airline' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-sm font-medium">
                        Passenger Last Name
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="e.g., Smith"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="capitalize"
                        data-testid="booking-lastname-input"
                      />
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                      <Globe className="h-4 w-4 inline mr-2" />
                      Searching directly from {GDS_OPTIONS.find(g => g.value === selectedSource)?.label}
                    </div>
                  </>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-brand-500 hover:bg-brand-600"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching {selectedSource !== 'local' ? GDS_OPTIONS.find(g => g.value === selectedSource)?.label : ''}...
                    </>
                  ) : selectedSource === 'airline' ? (
                    <>
                      <Globe className="h-4 w-4 mr-2" />
                      Open on Airline Website
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Find My Booking
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t">
                <p className="text-xs text-slate-500 text-center">
                  Your booking reference (PNR) can be found in your confirmation email
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Booking Details */
          <div className="space-y-6">
            {/* PNR Header */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Booking Reference</p>
                    <p className="text-4xl font-bold text-emerald-400 tracking-wider font-mono">{booking.pnr}</p>
                    {searchSource && (
                      <Badge className={`mt-2 ${
                        searchSource === 'local' ? 'bg-purple-500' : 
                        searchSource === 'sabre' ? 'bg-red-500' : 
                        searchSource === 'travelport' ? 'bg-blue-500' : 'bg-green-500'
                      }`}>
                        {searchSource === 'local' ? 'Flight380 Database' : searchSource.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-500 text-white px-4 py-1">
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      {booking.status || 'CONFIRMED'}
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>

            {/* Passengers */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-brand-600">
                  <Users className="h-5 w-5" />
                  Passengers ({booking.passengers?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {booking.passengers?.map((p, idx) => (
                    <div key={idx} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-semibold text-sm">
                          {idx + 1}
                        </span>
                        <span className="font-semibold">
                          {(p.title || 'MR').toUpperCase()} {(p.first_name || p.firstName || '').toUpperCase()} {(p.last_name || p.lastName || '').toUpperCase()}
                        </span>
                      </div>
                      <Badge variant="outline">{p.type || 'ADULT'}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Flight Details - Show ALL segments */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardHeader className="bg-blue-50 pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-brand-600">
                  <Plane className="h-5 w-5" />
                  Flight Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Outbound Segments */}
                <div className="p-6 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Outbound Flight</p>
                  {(booking.flight_data?.segments || booking.flight?.segments || []).length > 0 ? (
                    <div className="space-y-4">
                      {(booking.flight_data?.segments || booking.flight?.segments || []).map((seg, idx, arr) => (
                        <div key={idx}>
                          <div className="flex items-center justify-between">
                            <div className="text-center">
                              <p className="text-2xl font-bold">{formatTime(seg.departure_time)}</p>
                              <p className="text-sm text-slate-500">{formatDate(seg.departure_date)}</p>
                              <p className="text-lg font-semibold text-brand-600 mt-1">{seg.departure_airport}</p>
                            </div>
                            <div className="flex-1 px-6 text-center">
                              <p className="text-sm text-slate-500">{seg.duration || ''}</p>
                              <div className="h-0.5 bg-gradient-to-r from-brand-500 to-emerald-500 my-2 relative">
                                <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 text-brand-500 bg-white" />
                              </div>
                              <p className="text-xs text-slate-400">{seg.marketing_airline || seg.airline_code || ''}{seg.flight_number || ''}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold">{formatTime(seg.arrival_time)}</p>
                              <p className="text-sm text-slate-500">{formatDate(seg.arrival_date || seg.departure_date)}</p>
                              <p className="text-lg font-semibold text-brand-600 mt-1">{seg.arrival_airport}</p>
                            </div>
                          </div>
                          {idx < arr.length - 1 && (
                            <div className="flex items-center justify-center my-4">
                              <div className="bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Connection in {seg.arrival_airport}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <p className="text-2xl font-bold">{formatTime(booking.flight?.departure_time)}</p>
                        <p className="text-sm text-slate-500">{formatDate(booking.flight?.departure_date)}</p>
                        <p className="text-lg font-semibold text-brand-600 mt-1">{booking.flight?.from || booking.flight?.origin}</p>
                      </div>
                      <div className="flex-1 px-6 text-center">
                        <p className="text-sm text-slate-500">{booking.flight?.duration || 'N/A'}</p>
                        <div className="h-0.5 bg-gradient-to-r from-brand-500 to-emerald-500 my-2 relative">
                          <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 text-brand-500 bg-white" />
                        </div>
                        <p className="text-xs text-slate-400">{booking.flight?.airline || ''} {booking.flight?.flight_number || ''}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold">{formatTime(booking.flight?.arrival_time)}</p>
                        <p className="text-sm text-slate-500">{formatDate(booking.flight?.departure_date)}</p>
                        <p className="text-lg font-semibold text-brand-600 mt-1">{booking.flight?.to || booking.flight?.destination}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Return Segments */}
                {((booking.flight_data?.return_segments || booking.flight?.return_segments || []).length > 0 || booking.flight?.return_departure_time) && (
                  <div className="p-6 bg-slate-50">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Return Flight</p>
                    {(booking.flight_data?.return_segments || booking.flight?.return_segments || []).length > 0 ? (
                      <div className="space-y-4">
                        {(booking.flight_data?.return_segments || booking.flight?.return_segments || []).map((seg, idx, arr) => (
                          <div key={idx}>
                            <div className="flex items-center justify-between">
                              <div className="text-center">
                                <p className="text-2xl font-bold">{formatTime(seg.departure_time)}</p>
                                <p className="text-sm text-slate-500">{formatDate(seg.departure_date)}</p>
                                <p className="text-lg font-semibold text-brand-600 mt-1">{seg.departure_airport}</p>
                              </div>
                              <div className="flex-1 px-6 text-center">
                                <p className="text-sm text-slate-500">{seg.duration || ''}</p>
                                <div className="h-0.5 bg-gradient-to-r from-brand-500 to-emerald-500 my-2 relative">
                                  <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 text-brand-500 bg-white rotate-180" />
                                </div>
                                <p className="text-xs text-slate-400">{seg.marketing_airline || seg.airline_code || ''}{seg.flight_number || ''}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold">{formatTime(seg.arrival_time)}</p>
                                <p className="text-sm text-slate-500">{formatDate(seg.arrival_date || seg.departure_date)}</p>
                                <p className="text-lg font-semibold text-brand-600 mt-1">{seg.arrival_airport}</p>
                              </div>
                            </div>
                            {idx < arr.length - 1 && (
                              <div className="flex items-center justify-center my-4">
                                <div className="bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium">
                                  <Clock className="h-4 w-4 inline mr-2" />
                                  Connection in {seg.arrival_airport}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{formatTime(booking.flight?.return_departure_time)}</p>
                          <p className="text-sm text-slate-500">{formatDate(booking.flight?.return_departure_date)}</p>
                          <p className="text-lg font-semibold text-brand-600 mt-1">{booking.flight?.to || booking.flight?.destination}</p>
                        </div>
                        <div className="flex-1 px-6 text-center">
                          <p className="text-sm text-slate-500">{booking.flight?.return_duration || 'N/A'}</p>
                          <div className="h-0.5 bg-gradient-to-r from-brand-500 to-emerald-500 my-2 relative">
                            <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 text-brand-500 bg-white" />
                          </div>
                          <p className="text-xs text-slate-400">{booking.flight?.airline || ''}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold">{formatTime(booking.flight?.return_arrival_time)}</p>
                          <p className="text-sm text-slate-500">{formatDate(booking.flight?.return_arrival_date)}</p>
                          <p className="text-lg font-semibold text-brand-600 mt-1">{booking.flight?.from || booking.flight?.origin}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Price */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Amount Paid</p>
                    <p className="text-3xl font-bold text-emerald-400">
                      <Money gbp={booking.total_price?.toFixed(2) || booking.price?.toFixed(2) || '0.00'} />
                    </p>
                  </div>
                  <div className="text-right text-sm text-slate-400">
                    <p>{booking.passengers?.length || 1} Passenger(s)</p>
                    <p>All taxes included</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Button variant="outline" onClick={handlePrint} className="px-6">
                <Printer className="h-4 w-4 mr-2" />
                Print Itinerary
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDownloadPDF} 
                disabled={isGeneratingPDF}
                className="px-6 border-brand-500 text-brand-600 hover:bg-brand-50"
              >
                {isGeneratingPDF ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={openEmailModal}
                className="px-6 border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                data-testid="email-pdf-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowFlightStatus(true)}
                className="px-6 border-green-500 text-green-600 hover:bg-green-50"
                data-testid="track-flight-status-btn"
              >
                <Radio className="h-4 w-4 mr-2" />
                Track Flight Status
              </Button>
              <Button onClick={resetSearch} className="px-6 bg-brand-500 hover:bg-brand-600">
                Search Another Booking
              </Button>
            </div>

            {/* Email Modal */}
            {showEmailModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md border-0 shadow-2xl">
                  <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-t-lg relative">
                    <button 
                      onClick={() => setShowEmailModal(false)}
                      className="absolute top-4 right-4 text-white/80 hover:text-white"
                    >
                      <X className="h-5 w-5" />
                    </button>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Email E-Ticket
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    {emailSent ? (
                      <div className="text-center py-6">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-bold text-emerald-700 mb-2">Email Sent!</h3>
                        <p className="text-slate-600">Your e-ticket has been sent to {emailAddress}</p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="email-input" className="text-sm font-medium">
                            Enter your email address
                          </Label>
                          <Input
                            id="email-input"
                            type="email"
                            placeholder="your@email.com"
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                            className="h-12"
                            data-testid="email-input"
                          />
                          {emailError && (
                            <p className="text-sm text-red-500 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {emailError}
                            </p>
                          )}
                        </div>
                        
                        <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                          <p className="font-medium text-slate-700 mb-1">What you&apos;ll receive:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Professional PDF e-ticket itinerary</li>
                            <li>• Flight details & booking reference</li>
                            <li>• Important travel information</li>
                          </ul>
                        </div>
                        
                        <div className="flex gap-3">
                          <Button 
                            variant="outline" 
                            onClick={() => setShowEmailModal(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleSendEmail}
                            disabled={isSendingEmail || !emailAddress}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            data-testid="send-email-btn"
                          >
                            {isSendingEmail ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                Send Email
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Flight Status Modal */}
            {showFlightStatus && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                      <Radio className="h-5 w-5 text-green-600" />
                      Live Flight Status
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFlightStatus(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="p-4">
                    <FlightStatusTracker pnr={booking?.pnr} onClose={() => setShowFlightStatus(false)} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBooking;
